/**
 * Idempotency Helper
 *
 * Generates deterministic idempotency keys for UI actions.
 * Used by apiClient to ensure idempotent mutations.
 */

/**
 * Stable JSON stringify (deterministic key ordering)
 */
function stableStringify(obj) {
    if (obj === null) return "null";
    if (typeof obj !== "object") return JSON.stringify(obj);
    if (Array.isArray(obj)) return `[${obj.map(stableStringify).join(",")}]`;

    const keys = Object.keys(obj).sort();
    const pairs = keys.map(
        (k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`,
    );
    return `{${pairs.join(",")}}`;
}

/**
 * Simple hash function (djb2)
 */
function simpleHash(str) {
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) + hash + str.charCodeAt(i);
    }
    return (hash >>> 0).toString(36);
}

/**
 * Generate stable hash from object
 */
export function stableHash(obj) {
    const json = stableStringify(obj);
    return simpleHash(json);
}

/**
 * Generate idempotency key
 *
 * @param {string} action - Action name (e.g., 'deposit_create', 'withdraw_create')
 * @param {object} partsObj - Object containing key parts (user_id, amount, etc.)
 * @returns {string} Idempotency key in format: ui:{action}:{hash}
 */
export function idemKey(action, partsObj) {
    const hash = stableHash(partsObj);
    return `ui:${action}:${hash}`;
}

/**
 * Common idempotency key builders
 */
export const IdemKeys = {
    // Order operations
    orderCreate: (source, payload) =>
        idemKey("order_create", { source, ...payload }),
    orderValidate: (orderId) => idemKey("order_validate", { orderId }),

    // Payment operations
    attachPayment: (orderId, mode, methodId) =>
        idemKey("payment_attach", { orderId, mode, methodId }),
    submitProof: (paymentId, proofHash) =>
        idemKey("submit_proof", { paymentId, proofHash }),

    // Wallet operations
    depositCreate: (userId, amount, methodId, mode) =>
        idemKey("deposit_create", {
            userId,
            amount,
            methodId,
            mode,
            ts: Date.now(),
        }),
    withdrawCreate: (userId, amount, destination) =>
        idemKey("withdraw_create", {
            userId,
            amount,
            destination,
            ts: Date.now(),
        }),

    // Achievement & Referral
    // NOTE: For repeatable achievements, include timestamp to allow multiple claims
    achievementClaim: (userId, achievementId) =>
        idemKey("achievement_claim", { userId, achievementId, ts: Date.now() }),
    referralPayout: (eventId) => idemKey("referral_payout", { eventId }),

    // Games operations
    // Include ts so each attempt gets a fresh key. Without this, repeated clicks
    // (or retries after a provider failure) replay the server-cached response and
    // the UI keeps showing the OLD account details instead of reflecting the new
    // attempt's outcome.
    gameCreateAccount: (userId, gameId) =>
        idemKey("game_create_account", { userId, gameId, ts: Date.now() }),
    gameLoad: (userId, gameId, amount) =>
        idemKey("game_load", { userId, gameId, amount }),
    gameRedeem: (userId, gameId) => idemKey("game_redeem", { userId, gameId }),

    // Admin decisions
    adminPaymentDecision: (adminId, paymentId, decision) =>
        idemKey("admin_payment_decision", { adminId, paymentId, decision }),
    adminPayoutDecision: (adminId, payoutId, decision) =>
        idemKey("admin_payout_decision", { adminId, payoutId, decision }),

    // Generic
    generic: (action, parts) => idemKey(action, parts),
};

export default {
    stableHash,
    idemKey,
    IdemKeys,
};
